"use client"

import { useState, useEffect } from "react"
import { useForm, SubmitHandler } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useToast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog"
import { PatientSearch } from "@/components/patient-search"
import { Patient, Doctor } from "@/types"
import { Loader2 } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/utils/supabase/client"

const admissionSchema = z.object({
  patient_id: z.string().uuid("A patient must be selected."),
  doctor_id: z.string().uuid("A doctor must be selected."),
  initial_diagnosis: z.string().min(3, "Initial diagnosis is required."),
  room_number: z.string().optional(),
  bed_number: z.string().optional(),
})

type AdmissionFormData = z.infer<typeof admissionSchema>

interface AdmitPatientModalProps {
  isOpen: boolean
  onClose: () => void
  onAdmitSuccess: () => void
}

export function AdmitPatientModal({ isOpen, onClose, onAdmitSuccess }: AdmitPatientModalProps) {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm<AdmissionFormData>({
    resolver: zodResolver(admissionSchema),
  })

  useEffect(() => {
    if (selectedPatient) {
      setValue("patient_id", selectedPatient.id)
    }
  }, [selectedPatient, setValue])

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const { data: doctorsData, error } = await supabase
          .from("doctors")
          .select("*")
          .eq("is_available", true);

        if (error) throw error;
        setDoctors(doctorsData || []);
      } catch (error) {
        console.error('Error fetching doctors:', error);
        setDoctors([]); // Ensure doctors is always an array
        toast({ title: "Error", description: "Could not load available doctors.", variant: "destructive" });
      }
    };
    if (isOpen) {
      fetchDoctors();
    }
  }, [isOpen, toast]);

  const handleFormSubmit: SubmitHandler<AdmissionFormData> = async (data) => {
    setIsSubmitting(true)
    try {
      const admissionData = {
        ...data,
        organization_id: "ea9dcf29-57a3-4a6c-a299-1920b3441b83",
      }

      const { error } = await supabase.from("admissions").insert([admissionData]).select();

      if (error) throw error;

      toast({ title: "Patient Admitted", description: "The patient has been successfully admitted." });
      onAdmitSuccess();
      handleClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset()
    setSelectedPatient(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Admit New Patient</DialogTitle>
          <DialogDescription>
            Search for an existing patient and fill in the admission details.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <label>Search Patient</label>
            <PatientSearch onPatientSelect={setSelectedPatient} />
            {selectedPatient && <p className="text-sm text-muted-foreground mt-2">Selected: <span className="font-semibold">{selectedPatient.name} (MR: {selectedPatient.mr_number})</span></p>}
            {errors.patient_id && <p className="text-red-500 text-sm mt-1">{errors.patient_id.message}</p>}
          </div>

          <div>
            <label htmlFor="doctor_id">Assign Doctor</label>
            <Select onValueChange={(value) => setValue('doctor_id', value)}>
                <SelectTrigger><SelectValue placeholder="Select a doctor" /></SelectTrigger>
                <SelectContent>
                    {doctors.map(doc => <SelectItem key={doc.id} value={doc.id}>{doc.name} - {doc.specialization}</SelectItem>)}
                </SelectContent>
            </Select>
            {errors.doctor_id && <p className="text-red-500 text-sm mt-1">{errors.doctor_id.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="room_number">Room Number</label>
              <Input id="room_number" {...register("room_number")} />
            </div>
            <div>
              <label htmlFor="bed_number">Bed Number</label>
              <Input id="bed_number" {...register("bed_number")} />
            </div>
          </div>

          <div>
            <label htmlFor="initial_diagnosis">Initial Diagnosis / Reason for Admission</label>
            <Textarea id="initial_diagnosis" {...register("initial_diagnosis")} />
            {errors.initial_diagnosis && <p className="text-red-500 text-sm mt-1">{errors.initial_diagnosis.message}</p>}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Admit Patient
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
} 